import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common/Bits";
import { ROLE_LABEL } from "@/lib/constants";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Settings, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { can, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [catalogs, setCatalogs] = useState(null);

  useEffect(() => {
    if (!can.admin) return;
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/catalogs").then(({ data }) => setCatalogs(data));
  }, [can.admin]);

  if (!can.admin) {
    return <EmptyState icon={ShieldAlert} title="Acceso restringido" subtitle="Solo el rol Administrador puede acceder a la configuración administrativa." />;
  }

  const changeRole = async (id, role) => {
    try {
      await api.put(`/admin/users/${id}/role`, { role });
      toast.success("Rol actualizado · registrado en auditoría");
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Administración" subtitle="Usuarios, roles, catálogos maestros y parámetros de gobernanza." testId="admin-header" />
      <Tabs defaultValue="usuarios">
        <TabsList className="bg-muted">
          <TabsTrigger value="usuarios" data-testid="admin-tab-usuarios">Usuarios y roles</TabsTrigger>
          <TabsTrigger value="catalogos" data-testid="admin-tab-catalogos">Catálogos</TabsTrigger>
          <TabsTrigger value="parametros" data-testid="admin-tab-parametros">Parámetros</TabsTrigger>
        </TabsList>

        <TabsContent value="usuarios" className="mt-5">
          <div className="bg-card rounded-xl border border-border shadow-card overflow-x-auto">
            <Table data-testid="admin-users-table">
              <TableHeader><TableRow className="hover:bg-transparent">{["Usuario", "Cargo", "Departamento", "Rol"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wide">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary text-primary-foreground text-xs">{u.initials}</AvatarFallback></Avatar>
                        <div><p className="text-sm font-medium">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{u.title}</TableCell>
                    <TableCell className="text-sm">{u.department}</TableCell>
                    <TableCell>
                      {u.id === user.id ? <Badge variant="outline" className="bg-primary/8 text-primary border-primary/25">{ROLE_LABEL[u.role]} (usted)</Badge> : (
                        <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                          <SelectTrigger className="w-40 h-8 text-xs" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{Object.entries(ROLE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="catalogos" className="mt-5">
          {catalogs && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[["Departamentos", catalogs.departments], ["Proveedores", catalogs.providers], ["Tipos de iniciativa", catalogs.asset_types], ["Clasificaciones de datos", catalogs.data_classifications], ["Niveles de riesgo", catalogs.risk_levels], ["Estados del ciclo de vida", catalogs.stages]].map(([title, items]) => (
                <Card key={title} className="shadow-card border-border">
                  <CardHeader className="pb-2"><CardTitle className="font-display text-sm font-semibold">{title}</CardTitle></CardHeader>
                  <CardContent className="flex flex-wrap gap-1.5">
                    {items.map((i) => <span key={i} className="px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground text-xs">{i}</span>)}
                  </CardContent>
                </Card>
              ))}
              <Card className="shadow-card border-border sm:col-span-2 lg:col-span-3">
                <CardHeader className="pb-2"><CardTitle className="font-display text-sm font-semibold">Catálogo de controles ({catalogs.controls.length})</CardTitle></CardHeader>
                <CardContent className="flex flex-wrap gap-1.5">
                  {catalogs.controls.map((c) => <span key={c.id} className="px-2.5 py-1 rounded-md bg-accent/10 text-accent border border-accent/20 text-xs">{c.name} · {c.category}</span>)}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="parametros" className="mt-5">
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              ["Quórum del Comité de IA", "3 votos válidos", "Decisiones cerradas al alcanzar quórum, resultado por mayoría"],
              ["Vigencia de evaluaciones", "12 meses", "Reevaluación automática por vencimiento anual"],
              ["Reglas de reevaluación", "12 gatillantes activos", "Cambio de modelo, proveedor, volumen, datos, incidentes, normativa…"],
              ["Iniciativas clínicas", "Validación reforzada", "Documento de validación clínica obligatorio en el dossier"],
              ["Retención de auditoría", "Inmutable", "Los eventos y evidencias usados en decisiones no se eliminan"],
              ["Plantillas de evaluación", "15 dimensiones", "Privacidad, seguridad, impacto clínico, sesgo, autonomía…"],
            ].map(([t, v, d]) => (
              <Card key={t} className="shadow-card border-border">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground"><Settings className="h-3.5 w-3.5" />{t}</div>
                  <p className="font-display text-lg font-semibold mt-2">{v}</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{d}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
